<template>
    <component
        :is="renderComponent"
        :to="isNuxtLink ? cleanedUrl : undefined"
        :href="!isNuxtLink ? cleanedUrl : undefined"
        :target="renderComponent === 'a' ? '_blank' : undefined"
        :class="mainClassesMerged"
        @click="handleClick"
    >
        <div
            v-if="hasImageSlot || card.image"
            class="relative"
            :class="imageContainerClasses"
        >
            <slot name="image">
                <StoryblokImage
                    v-if="card.image"
                    :src="card.image.src"
                    :alt="card.image.alt"
                    class="h-full w-full object-cover"
                    :class="imageClasses"
                    itemprop="image"
                    :sizes="card.image.sizes ?? '342px'"
                    :image-width="card.image.width ?? 352"
                    :image-height="card.image.height ?? 352"
                    :quality="card.image.quality ?? 90"
                />
            </slot>
        </div>

        <div
            class="flex flex-1 flex-col justify-between"
            :class="contentClasses"
        >
            <div class="flex flex-col" :class="contentInnerClasses">
                <slot name="tags">
                    <TagGroup
                        v-if="card.tags"
                        :tags="card.tags"
                        class="mb-2 flex-wrap"
                    />
                </slot>

                <slot name="title">
                    <div
                        v-if="card.title"
                        class="mb-3.5 text-xl font-bold"
                        itemprop="headline"
                    >
                        {{ card.title }}
                    </div>
                </slot>

                <slot name="subtitle">
                    <div
                        v-if="card.shortDescription"
                        class="mb-2.5 text-sm font-bold text-drip"
                    >
                        {{ card.shortDescription }}
                    </div>
                </slot>

                <slot name="description">
                    <WysiwygContent
                        v-if="card.description"
                        class="mb-4"
                        :content="card.description"
                    />
                </slot>
            </div>

            <div :class="footerClasses">
                <slot name="footer">
                    <div
                        v-if="renderComponent !== NuxtLink"
                        class="group-hover:!underline"
                    >
                        <NuxtLink
                            v-if="card.link"
                            :to="urlFromStoryblokLinkField(card.link)"
                        >
                            {{ card.link.label }}
                        </NuxtLink>
                    </div>
                    <div v-else class="text-brew group-hover:!underline">
                        <span v-if="card.link">
                            {{ card.link.label }}
                        </span>
                    </div>
                </slot>
            </div>
        </div>
    </component>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components';
import type { CardType } from '#shared/types/ui/CardType';
import { urlFromStoryblokLinkField } from '~/utils/helpers';

const props = withDefaults(
    defineProps<{
        card: CardType;
        size?: string;
        as?: string;
        mainClasses?: string;
        imageContainerClasses?: string;
        imageClasses?: string;
        contentClasses?: string;
        contentInnerClasses?: string;
        footerClasses?: string;
    }>(),
    {
        size: 'small',
        as: 'div',
        mainClasses: 'flex-col',
        imageContainerClasses: 'rounded-t-[10px] h-[235px] bg-gray-100',
        imageClasses: 'rounded-t-[10px] overflow-hidden',
        contentClasses: 'p-6',
        contentInnerClasses: undefined,
        footerClasses: 'mt-4',
    },
);

const slots = useSlots();
const hasImageSlot = computed(() => !!slots.image);
const cleanedUrl = computed(() => urlFromStoryblokLinkField(props.card.link));

const isNuxtLink = computed(() => {
    return props.as === 'NuxtLink' || props.card.link?.linktype === 'story';
});

const renderComponent = computed(() => {
    if (isNuxtLink.value) {
        return resolveComponent('NuxtLink');
    }

    if (props.card.link?.linktype === 'url') {
        return 'a';
    }

    return props.as;
});

const mainClassesMerged = computed(() => [
    'group flex h-full w-full rounded-xl bg-white text-grind shadow-card',
    props.mainClasses,
]);

const handleClick = (event: MouseEvent) => {
    if (props.card.link?.anchor) {
        event.preventDefault();
        scrollToId(props.card.link.anchor);
    }
};
</script>
